/**
 * Paredes na tela do mestre (`docs/spec/scene-walls.md`): cada parede colorida pelo tipo, as
 * selecionadas em destaque, os vértices na ferramenta de seleção e o rascunho do que está sendo
 * desenhado. Não recebe clique: a ferramenta "Paredes" acha as paredes pela geometria.
 */
import { useLongPress } from '../ui/touch.js';
import { presetOf, type Wall, type WallPreset } from './walls.js';

/** Cor de cada tipo (feita à mão: branco). */
export const WALL_COLORS: Record<WallPreset | 'custom', string> = {
  wall: '#ff5a3c',
  window: '#4fc3f7',
  ethereal: '#b39ddb',
  terrain: '#8bc34a',
  door: '#ffb300',
  secret: '#e040fb',
  custom: '#ffffff',
};

export const wallColor = (wall: Wall): string => WALL_COLORS[presetOf(wall) ?? 'custom'];

const pointsAttr = (points: number[], g: number): string => {
  const out: string[] = [];
  for (let i = 0; i + 1 < points.length; i += 2) out.push(`${points[i]! * g},${points[i + 1]! * g}`);
  return out.join(' ');
};

export type WallDraft = { points: number[]; closed: boolean } | null;

export function WallsLayer({
  walls,
  g,
  zoom,
  selected,
  showVertices,
  draft,
  box,
}: {
  walls: Wall[];
  g: number;
  zoom: number;
  selected: ReadonlySet<string>;
  showVertices: boolean;
  /** linha ou forma sendo desenhada, em células */
  draft: WallDraft;
  /** caixa de seleção ou de corte, em células */
  box: { x: number; y: number; w: number; h: number; cut: boolean } | null;
}) {
  const px = 1 / zoom;
  return (
    <g className="rd-walls" pointerEvents="none">
      {walls.map((wall) => {
        const Shape = wall.closed ? 'polygon' : 'polyline';
        const on = selected.has(wall.id);
        const color = wallColor(wall);
        return (
          <g key={wall.id} data-wall={wall.id} data-wall-type={presetOf(wall) ?? 'custom'} className={on ? 'is-selected' : undefined}>
            <Shape points={pointsAttr(wall.points, g)} fill="none" stroke="#000" strokeOpacity={0.55} strokeWidth={(on ? 8 : 6) * px} strokeLinejoin="round" strokeLinecap="round" />
            <Shape
              points={pointsAttr(wall.points, g)}
              fill="none"
              stroke={on ? '#ffffff' : color}
              strokeWidth={(on ? 4 : 3) * px}
              strokeDasharray={wall.open ? `${6 * px} ${5 * px}` : undefined}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            {showVertices
              ? Array.from({ length: wall.points.length / 2 }, (_, i) => (
                  <circle key={i} cx={wall.points[i * 2]! * g} cy={wall.points[i * 2 + 1]! * g} r={(on ? 5 : 3.5) * px} fill={on ? '#ffffff' : color} stroke="#000" strokeWidth={px} />
                ))
              : null}
          </g>
        );
      })}
      {draft && draft.points.length >= 2 ? (
        <g className="rd-walls__draft">
          {draft.closed ? (
            <polygon points={pointsAttr(draft.points, g)} fill="none" stroke="#fff" strokeWidth={3 * px} strokeDasharray={`${6 * px} ${4 * px}`} />
          ) : (
            <polyline points={pointsAttr(draft.points, g)} fill="none" stroke="#fff" strokeWidth={3 * px} strokeDasharray={`${6 * px} ${4 * px}`} strokeLinecap="round" />
          )}
          {Array.from({ length: draft.points.length / 2 }, (_, i) => (
            <circle key={i} cx={draft.points[i * 2]! * g} cy={draft.points[i * 2 + 1]! * g} r={3.5 * px} fill="#fff" />
          ))}
        </g>
      ) : null}
      {box ? (
        <rect
          className={`rd-walls__box${box.cut ? ' is-cut' : ''}`}
          x={box.x * g}
          y={box.y * g}
          width={box.w * g}
          height={box.h * g}
          strokeWidth={1.5 * px}
          strokeDasharray={`${5 * px} ${4 * px}`}
        />
      ) : null}
    </g>
  );
}

/**
 * Ícones das portas: no meio do segmento, com a cor do estado (amarela fechada, verde aberta,
 * vermelha trancada) e um anel roxo na porta secreta (só o mestre a vê como porta). Clicar abre ou
 * fecha; o botão direito (mestre) tranca e destranca.
 *
 * O cadeado é coisa do mestre: na visão dele aparece sempre; para os jogadores (e para o mestre
 * olhando como um deles) a porta trancada parece fechada, e o cadeado só aparece por um instante
 * para quem tentou abrir (`flashing`).
 */
export function DoorsLayer({
  doors,
  g,
  zoom,
  interactive,
  showLocks,
  flashing,
  labels,
  onToggle,
  onLock,
}: {
  doors: Wall[];
  g: number;
  zoom: number;
  interactive: boolean;
  /** mostra sempre o cadeado das trancadas (o mestre na visão de mestre) */
  showLocks: boolean;
  /** portas trancadas que alguém acabou de tentar abrir: o cadeado aparece por um instante */
  flashing: ReadonlySet<string>;
  labels: { open: string; close: string; locked: string };
  onToggle: (wall: Wall) => void;
  onLock?: (wall: Wall) => void;
}) {
  const r = 11 / zoom;
  // trancar é o botão direito, ou o dedo segurando a porta
  const press = useLongPress();
  return (
    <g className="rd-doors" pointerEvents={interactive ? 'auto' : 'none'}>
      {doors.map((wall) => {
        const cx = ((wall.points[0]! + wall.points[2]!) / 2) * g;
        const cy = ((wall.points[1]! + wall.points[3]!) / 2) * g;
        const locked = wall.locked && (showLocks || flashing.has(wall.id));
        const state = wall.open ? 'open' : locked ? 'locked' : 'closed';
        const color = state === 'open' ? '#5fbf4a' : state === 'locked' ? '#e5413b' : '#ffb300';
        const label = state === 'open' ? labels.close : state === 'locked' ? labels.locked : labels.open;
        const hold = press.bind(() => interactive && onLock?.(wall));
        return (
          <g
            key={wall.id}
            className={`rd-door is-${state}`}
            data-door={wall.id}
            data-door-state={state}
            data-door-secret={wall.door === 'secret' ? 'true' : undefined}
            transform={`translate(${cx} ${cy})`}
            role={interactive ? 'button' : undefined}
            aria-label={interactive ? label : undefined}
            onPointerDown={(e) => {
              if (onLock) hold.onPointerDown(e);
              // não vira arrasto da vista nem seleção; o botão direito também (senão a vista
              // captura o ponteiro e o menu de contexto não chega à porta). Alt + clique é ping
              if (interactive && (e.button === 2 || (e.button === 0 && !e.altKey))) e.stopPropagation();
            }}
            onPointerMove={hold.onPointerMove}
            onPointerUp={hold.onPointerUp}
            onPointerCancel={hold.onPointerCancel}
            // o clique que vem depois de segurar não abre a porta que acabou de ser trancada
            onClickCapture={hold.onClickCapture}
            onClick={(e) => {
              if (!interactive || e.altKey) return;
              e.stopPropagation();
              onToggle(wall);
            }}
            onContextMenu={hold.onContextMenu}
          >
            <title>{label}</title>
            <circle r={r} fill="#1b1b1b" fillOpacity={0.85} stroke={wall.door === 'secret' ? '#e040fb' : color} strokeWidth={2.5 / zoom} />
            {state === 'locked' ? (
              <path
                d={`M${-4 / zoom} ${-1 / zoom}v${-2 / zoom}a${4 / zoom} ${4 / zoom} 0 0 1 ${8 / zoom} 0v${2 / zoom}M${-5 / zoom} ${-1 / zoom}h${10 / zoom}v${7 / zoom}h${-10 / zoom}z`}
                fill="none"
                stroke={color}
                strokeWidth={1.6 / zoom}
              />
            ) : (
              <path
                d={state === 'open' ? `M${-5 / zoom} ${-6 / zoom}v${12 / zoom}M${-5 / zoom} ${-6 / zoom}l${8 / zoom} ${-2 / zoom}v${16 / zoom}l${-8 / zoom} ${-2 / zoom}` : `M${-5 / zoom} ${-7 / zoom}h${10 / zoom}v${14 / zoom}h${-10 / zoom}zM${2 / zoom} 0h${0.1 / zoom}`}
                fill="none"
                stroke={color}
                strokeWidth={1.6 / zoom}
                strokeLinecap="round"
              />
            )}
          </g>
        );
      })}
    </g>
  );
}
